// Puente de SOLO LECTURA hacia `catalog` para selectores de VM/subsistema/BD/SAP (spec.md §6,
// plan.md §3.3, tasks.md T28). Todo el acceso a red vive en infrastructure/ (regla #3). §7-I:
// `catalog` ya está migrado pero su `index.ts` hoy solo exporta tipos, no hooks de lectura —
// puente `zod`-ligero autocontenido, mismo precedente que
// `cloud/infrastructure/selectorOptions.repository.ts`. Condición de retiro: cuando
// `@modules/catalog` exponga hooks de lectura en su `index.ts`.

import { z } from 'zod';
import apiClient from '@shared/api/http';

const IdSchema = z.union([z.string(), z.number()]).transform(String);

// Forma mínima compartida por las 5 entidades de catálogo — no replica el DTO completo de
// `catalog/infrastructure/catalog.repository.ts` (category/vendor/is_active/family/service_pack/...):
// este puente solo alimenta selectores (id + etiqueta), no formularios de catálogo.
const CatalogOptionSchema = z.object({
  id: IdSchema,
  name: z.string(),
  version: z.string().nullish(),
  edition: z.string().nullish(),
});
export type CatalogOption = z.infer<typeof CatalogOptionSchema>;

// `platform_id` se conserva nullish y en snake_case (puente autocontenido, no `domain/`): lo
// consume `filterOsByPlatform` (domain/vm.rules.ts, T15) tras normalizarlo en `application/`.
// v1 lo devuelve como `platformId`, de ahí el alias de lectura.
const OperatingSystemOptionSchema = CatalogOptionSchema.extend({
  platformId: IdSchema.nullish(),
}).transform(({ platformId, ...rest }) => ({ ...rest, platform_id: platformId }));
export type OperatingSystemOption = z.infer<typeof OperatingSystemOptionSchema>;

export const catalogOptionsRepository = {
  async getOperatingSystems(): Promise<OperatingSystemOption[]> {
    const { data } = await apiClient.get('/operating-systems');
    return z.array(OperatingSystemOptionSchema).parse(data ?? []);
  },

  async getPlatforms(): Promise<CatalogOption[]> {
    const { data } = await apiClient.get('/platforms');
    return z.array(CatalogOptionSchema).parse(data ?? []);
  },

  // UC-21 (`VMDetailPage.jsx:1585-1593`): catálogo de subsistemas para el Autocomplete de "asignar".
  async getSubsystems(): Promise<CatalogOption[]> {
    const { data } = await apiClient.get('/subsystems');
    return z.array(CatalogOptionSchema).parse(data ?? []);
  },

  async getDatabaseSystems(): Promise<CatalogOption[]> {
    const { data } = await apiClient.get('/database-systems');
    return z.array(CatalogOptionSchema).parse(data ?? []);
  },

  async getSapProducts(): Promise<CatalogOption[]> {
    const { data } = await apiClient.get('/sap-products');
    return z.array(CatalogOptionSchema).parse(data ?? []);
  },
};
